import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft, Search, Upload } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TeacherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = React.useRef(null);

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const [formData, setFormData] = useState({
    udise: '',
    school_name: '',
    school_id: '',
    title: 'Mr',
    first_name: '',
    last_name: '',
    gender: 'Male',
    phone: '',
    email: '',
    address: '',
    subject_id: '',
    dob: '',
    marital_status: '',
    spouse_name: '',
    pan: '',
    aadhaar: '',
    experience_years: '',
    experience_months: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchSubjects();
    if (isEdit) {
      fetchTeacher();
    }
  }, [id]);

  const fetchSubjects = async () => {
    try {
      const res = await api.get('/config/subject');
      setSubjects(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTeacher = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/teachers/${id}`);
      if (res.data) {
        setFormData({
          ...res.data,
          subject_id: res.data.subject_id || '',
          school_id: res.data.school_id || '',
          dob: res.data.dob ? res.data.dob.split('T')[0] : '',
          marital_status: res.data.marital_status || '',
          spouse_name: res.data.spouse_name || '',
          pan: res.data.pan || '',
          aadhaar: res.data.aadhaar || '',
          experience_years: res.data.experience_years || '',
          experience_months: res.data.experience_months || '',
          status: res.data.status || 'Active'
        });
        if (res.data.profile_photo) {
          setPhotoPreview(`${BASE_URL.replace('/api', '')}${res.data.profile_photo}`);
        }
      }
    } catch (err) {
      toast.error('Failed to load teacher');
    } finally {
      setLoading(false);
    }
  };

  const handleUdiseSearch = async () => {
    if (!formData.udise) return toast.error('Please enter a UDISE number');
    try {
      const res = await api.get(`/teachers/udise/${formData.udise}`);
      setFormData(prev => ({ ...prev, school_id: res.data.id, school_name: res.data.name }));
      toast.success('School found');
    } catch (err) {
      setFormData(prev => ({ ...prev, school_id: '', school_name: '' }));
      toast.error('School not found for this UDISE');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name) {
      return toast.error('First Name is required');
    }
    
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key]);
    });
    if (photoFile) {
      submitData.append('profile_photo', photoFile);
    }

    try {
      if (isEdit) {
        await api.put(`/teachers/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Teacher updated successfully');
      } else {
        await api.post('/teachers', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Teacher added successfully');
      }
      navigate('/teachers');
    } catch (err) {
      toast.error('Failed to save teacher');
    }
  };

  if (loading) return <AppLayout title="Edit Teacher"><div style={{ padding: 40, textAlign: 'center' }}>Loading...</div></AppLayout>;

  return (
    <AppLayout title={isEdit ? 'Edit Teacher' : 'Add Teacher'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate('/teachers')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
        >
          <ArrowLeft size={18} /> Back
        </button>

      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, padding: 32 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
            
            {/* School Info */}
            <div style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>School Assignment</h3>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>UDISE Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input 
                  type="text" 
                  value={formData.udise} 
                  onChange={e => setFormData({...formData, udise: e.target.value})} 
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} 
                  placeholder="Enter UDISE number" 
                />
                <button type="button" onClick={handleUdiseSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', background: C.g100, border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', color: C.g700, fontWeight: 500 }}>
                  <Search size={16} /> Search
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>School Name</label>
              <input 
                type="text" 
                value={formData.school_name} 
                onChange={e => setFormData({...formData, school_name: e.target.value})} 
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: formData.school_id ? C.g50 : '#fff' }} 
                placeholder="Auto-populated or enter manually" 
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Status</label>
              <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Personal Info */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Personal Details</h3>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 24, marginBottom: 8 }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: C.g100, border: `1px dashed ${C.g300}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Upload size={24} color={C.g400} />
                )}
              </div>
              <div>
                <label style={{ display: 'inline-block', padding: '8px 16px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: C.g700 }}>
                  Upload Photo
                  <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                </label>
                <div style={{ fontSize: 12, color: C.g500, marginTop: 8 }}>Square image recommended (JPG, PNG)</div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Title</label>
              <select value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
                <option value="Dr">Dr</option>
                <option value="Prof">Prof</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Gender</label>
              <select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>First Name *</label>
              <input type="text" value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="First name" required />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Last Name</label>
              <input type="text" value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Last name" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Date of Birth</label>
              <input type="date" value={formData.dob} onChange={e => setFormData({...formData, dob: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Marital Status</label>
              <select value={formData.marital_status} onChange={e => setFormData({...formData, marital_status: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="">Select Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>

            {formData.marital_status === 'Married' && (
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Spouse Name</label>
                <input type="text" value={formData.spouse_name} onChange={e => setFormData({...formData, spouse_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Spouse name" />
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>PAN Number</label>
              <input type="text" value={formData.pan} onChange={e => setFormData({...formData, pan: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="PAN number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Aadhaar Number</label>
              <input type="text" value={formData.aadhaar} onChange={e => setFormData({...formData, aadhaar: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Aadhaar number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Experience</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <input type="number" min="0" value={formData.experience_years} onChange={e => setFormData({...formData, experience_years: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Years" />
                </div>
                <div style={{ flex: 1 }}>
                  <input type="number" min="0" max="11" value={formData.experience_months} onChange={e => setFormData({...formData, experience_months: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Months" />
                </div>
              </div>
            </div>

            {/* Contact & Professional Info */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Contact & Professional</h3>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Phone Number</label>
              <input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Phone number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Email Address</label>
              <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Email address" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Subject</label>
              <select value={formData.subject_id} onChange={e => setFormData({...formData, subject_id: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="">Select Subject</option>
                {subjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Address</label>
              <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, minHeight: 80, resize: 'vertical' }} placeholder="Full address" />
            </div>

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.g100}` }}>
            <button
              type="button"
              onClick={() => navigate('/teachers')}
              style={{ padding: '12px 24px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
            >
              <Save size={18} /> {isEdit ? 'Update Teacher' : 'Save Teacher'}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default TeacherForm;
